import React, {FC, useState, useEffect} from "react"
import {Row, Col} from "react-bootstrap"
import PropTypes from "prop-types"

import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import {H2, H4, Body} from "@irati/ui/src/Typography"
import DragAndDrop from "@irati/ui/src/DragAndDrop"
import Flex from "@irati/ui/src/Flex"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"
import History from "@irati/utils/history"
import authentication from "@irati/auth"
import Loading from "@irati/ui/src/Loading"
import workosService from "@irati/workos/services/workos"
import {WorkOsOrganisation} from "@irati/types"

import {CircledConnector} from "../AddUsers/AddPeopleCSV/styled"
import FieldsRecap from "../AddUsers/AddPeopleCSV/FieldsRecap"
import {SETTINGS_LOGIN_INTEGRATION_DSYNC} from "../../../routes/routeConfig"
import DownloadEditCsvTemplate from "./DownloadEditCsvTemplate"

interface Props extends InjectIntl {
  onDrop: (acceptedFiles?: any[]) => void
}

const EditUsersCSV: FC<Props> = ({onDrop, formatMessage}, context) => {
  const [showInfoBanner, setInfoBanner] = useState(false)
  const [showWarningBanner, setWarningBanner] = useState(false)
  const [hasIntegration, setHasIntegration] = useState(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)

  const theme = useTheme()

  const handleInfoBanner = () => {
    History.push(SETTINGS_LOGIN_INTEGRATION_DSYNC, context)
  }

  const handleWarningBanner = () => {
    const url = formatMessage({id: "admin.components.updatePeopleCSV.integrationAlert.link"})
    window.open(url, "_blank")
  }

  useEffect(() => {
    const businessId = authentication.business().uuid
    const loaded = workosService
      .getOrganisation(businessId)
      .then((workOSOrganisation: WorkOsOrganisation) => {
        if (
          (workOSOrganisation.directoryId && workOSOrganisation.directoryId !== "") ||
          (workOSOrganisation.connectionId && workOSOrganisation.connectionId !== "")
        ) {
          setHasIntegration(true)
          setWarningBanner(true)
        } else {
          setInfoBanner(true)
        }
      })
      .catch(() => setInfoBanner(true))
    setLoad(loaded)
  }, [])

  return (
    <Loading load={load} useLoader={false}>
      <Flex justifyContent="flex-start" direction="column">
        {showInfoBanner && (
          <Alert
            type={AlertTypes.info}
            title="admin.components.updatePeopleCSV.alert.title"
            link="admin.components.updatePeopleCSV.alert.link"
            onHide={() => setInfoBanner(false)}
            onClickLink={handleInfoBanner}
          />
        )}
        {showWarningBanner && (
          <Alert
            type={AlertTypes.warning}
            title="admin.components.updatePeopleCSV.integrationAlert.title"
            link="admin.components.updatePeopleCSV.integrationAlert.btnLink"
            onHide={() => setWarningBanner(false)}
            onClickLink={handleWarningBanner}
          />
        )}
        <H2 marginBottom={2}>
          <FormattedMessage id="admin.components.updatePeopleCSV.header" />
        </H2>
        <H4>
          <FormattedMessage id="admin.components.updatePeopleCSV.text" />
        </H4>
        <H4 marginBottom={2} color={theme.colors.error}>
          <FormattedMessage id="admin.components.updatePeopleCSV.text2" />
        </H4>
        <CircledConnector
          data={[
            "renderCompFromProps",
            "renderCompFromProps",
            formatMessage({id: "admin.components.updatePeopleCSV.circledConnector.dragAndDrop"}),
            formatMessage({id: "admin.components.updatePeopleCSV.circledConnector.mapYourData"})
          ]}
          comp={[
            <DownloadEditCsvTemplate hasIntegration={hasIntegration} />,
            <Body maxWidth="100%">
              <FormattedMessage
                id="admin.components.updatePeopleCSV.circledConnector.fillInformation"
                values={{
                  warning: (
                    <Body weight="semibold" color={theme.colors.utility.error} maxWidth="100%">
                      <FormattedMessage id="admin.components.updatePeopleCSV.circledConnector.warning" />
                    </Body>
                  )
                }}
              />
            </Body>
          ]}
        />
        <Row>
          <Col md={12}>
            <DragAndDrop onDrop={onDrop} />
            <FieldsRecap updateUsers />
          </Col>
        </Row>
      </Flex>
    </Loading>
  )
}

EditUsersCSV.contextTypes = {
  appPath: PropTypes.string
}

export default injectIntl(EditUsersCSV)
